import { IWorkflowStep, IWorkflowStepInput, StepType } from "@/interfaces/workflow-step.interface";
import { number, object, string } from "yup";

import { WorkflowEditDialogMessages } from "@/messages/workflow/workflow-edit-dialog.messages";

const ALLOW_TRANSFER_KEYS = [
  "chatTransferAllowed",
  "ChatTransferAllowed",
  "allowAttendantTransfer",
  "AllowAttendantTransfer",
  "allowTransferAttendant",
  "AllowTransferAttendant",
];

export const readAllowAttendantTransfer = (
  workflowStep: IWorkflowStep | Record<string, unknown> | null | undefined
): boolean | null => {
  if (!workflowStep) return null;

  const source = workflowStep as Record<string, unknown>;
  for (const key of ALLOW_TRANSFER_KEYS) {
    const value = source[key];
    if (value === true || value === "true" || value === 1) return true;
    if (value === false || value === "false" || value === 0) return false;
  }

  return null;
};

export const buildWorkflowStepFormInitialData = (
  workflowStep: IWorkflowStep | null,
  workflowSteps: IWorkflowStep[] = []
): IWorkflowStepInput => {
  const hasSteps = workflowSteps.length > 0;
  const maxStepNum = hasSteps ? Math.max(...workflowSteps.map((s) => s.stepNumber)) : 0;
  const stepNumber = workflowStep?.stepNumber ?? maxStepNum + 1;

  return {
    stepName: workflowStep ? workflowStep.stepName : "",
    stepType: workflowStep?.stepType ? workflowStep.stepType : StepType.StepTypeS,
    allowAttendantTransfer: readAllowAttendantTransfer(workflowStep),
    startMessage: workflowStep ? workflowStep.startMessage : "",
    messageBody: workflowStep ? workflowStep.messageBody : "",
    menuOption: workflowStep ? workflowStep.menuOption : "",
    workflowEngineId: workflowStep?.workflowEngineId ?? 0,
    exceptionMessage: workflowStep ? workflowStep.exceptionMessage : "",
    exceptionSourceWorkflowStepName: workflowStep?.exceptionSourceWorkflowStepName || "",
    workflowElementId: workflowStep ? workflowStep.workflowElementId : 1,
    gotoWorkflowStepName: workflowStep ? workflowStep.gotoWorkflowStepName : "",
    stepNumber,
    workflowPositionX: workflowStep?.workflowPositionX || 0,
    workflowPositionY: workflowStep?.workflowPositionY || 0,
    startMessageEngine: workflowStep ? workflowStep.startMessageEngine : "",
    endMessageEngine: workflowStep?.endMessageEngine || "",
    promptAIInputId: workflowStep?.promptAIInputId ?? null,
    promptAIExceptionId: workflowStep?.promptAIExceptionId ?? null,
    chatTransferAllowed: workflowStep?.chatTransferAllowed ?? false,
  };
};

const AVATAR_TAG = "#AVATAR#";

export const isValidAvatarUsage = (value?: string | null): boolean => {
  if (!value || !value.toUpperCase().includes(AVATAR_TAG)) return true;
  return value.trim().toUpperCase() === AVATAR_TAG;
};

export const buildWorkflowStepFormSchema = (
  messages: WorkflowEditDialogMessages,
  isEditing: boolean
) =>
  object().shape({
    workflowElementId: number()
      .required(messages.errorRequiredWorkflowElement)
      .nullable(),
    stepType: string().required(messages.errorRequiredStepType),
    stepName: string().when([], {
      is: () => !isEditing,
      then: (schema) => schema.required(messages.errorRequiredStepName),
      otherwise: (schema) => schema.notRequired(),
    }),
    workflowEngineId: number().when("stepType", (stepType, schema) => {
      const isDynamic = Array.isArray(stepType)
        ? stepType[0] === StepType.StepTypeD
        : stepType === StepType.StepTypeD;
      return isDynamic
        ? schema
            .nullable()
            .test(
              "is-not-zero",
              messages.errorRequiredWorkflowEngine,
              (value) => value !== 0 && value != null
            )
        : schema.nullable().notRequired();
    }),
    messageBody: string()
      .nullable()
      .test("avatar-exclusive-usage", messages.errorAvatarExclusiveUsage, isValidAvatarUsage),
  });

const convertZeroToNull = (value: number | null | undefined) =>
  value === 0 || value === undefined ? null : value;

export const normalizeWorkflowStepFormData = (
  formData: IWorkflowStepInput,
  workflowId: number
): IWorkflowStepInput => {
  const allowAttendantTransfer = formData.allowAttendantTransfer ?? null;

  return {
    ...formData,
    workflowId: workflowId,
    workflowElementId: convertZeroToNull(formData.workflowElementId),
    workflowEngineId: convertZeroToNull(formData.workflowEngineId),
    exceptionMessage: formData.exceptionMessage,
    workflowPositionX: formData.workflowPositionX || 0,
    workflowPositionY: formData.workflowPositionY || 0,
    promptAIInputId: convertZeroToNull(formData.promptAIInputId),
    promptAIExceptionId: convertZeroToNull(formData.promptAIExceptionId),
    allowAttendantTransfer,
    chatTransferAllowed: allowAttendantTransfer,
  };
};
